import type { SerializedStyles } from '@emotion/core';
import { css } from '@emotion/core';

interface InterstitialStyleOverridesArgs {
  cookieBannerHeight: number;
  isForcedGuest: boolean;
  isFullScreenModal: boolean;
  mobileQueryWidth: number;
  customInterstitialWidth?: number;
  customInterstitialHeight?: number;
}

const screenWPSFlow = css`
  border-radius: unset;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const wpsHeaderStyles = ({
  isFullScreenModal,
}: {
  isFullScreenModal: boolean;
}): SerializedStyles => css`
  position: ${isFullScreenModal && 'unset'};
  margin-bottom: unset;
`;

const interstitialLoadingStyles = css`
  button {
    display: none;
  }
`;

const baseHeaderStyles = css`
  height: 2.75rem;
  margin: 0.313rem 0.625rem 0 0;
  padding: 0.5rem 0.5rem 0 0;
`;

export const getHeaderStyleOverrides = ({
  isLoading,
  isFullScreenModal,
}: {
  isLoading: boolean | undefined;
  isFullScreenModal: boolean | undefined;
}): SerializedStyles | SerializedStyles[] => {
  if (isLoading) {
    return css([baseHeaderStyles, interstitialLoadingStyles]);
  }
  if (isFullScreenModal) {
    return [baseHeaderStyles, wpsHeaderStyles({ isFullScreenModal })];
  }

  return baseHeaderStyles;
};

export const getInterstitialStyleOverrides = ({
  cookieBannerHeight = 0,
  customInterstitialHeight,
  customInterstitialWidth,
  isForcedGuest,
  isFullScreenModal,
  mobileQueryWidth = 420,
}: InterstitialStyleOverridesArgs): SerializedStyles => css`
  border-radius: 0.75rem;
  height: ${customInterstitialHeight
    ? `${customInterstitialHeight}rem`
    : 'auto'};
  max-height: 0;
  max-width: ${customInterstitialWidth
    ? `${customInterstitialWidth}rem`
    : '23.478rem'};
  overflow: auto;
  padding: ${isFullScreenModal ? '2rem 0 1rem' : '0'};

  &.xo-rc__open-interstitial {
    max-height: unset;
  }

  /* landscape mode for phones */
  @media (max-height: 420px) {
    max-width: unset;
    height: 90vh;
    width: 375px;
  }

  @media screen and (max-width: ${mobileQueryWidth}px) {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    bottom: 0;
    height: auto;
    max-width: unset;
    padding: 0;
    position: unset;
    width: 100%;
    ${isFullScreenModal && screenWPSFlow};

    &.xo-rc__open-interstitial {
      max-height: ${!isForcedGuest ? '480px' : '540px'};
      ${isFullScreenModal &&
      css`
        min-height: 100vh;
      `};
    }

    .ccpaCookieBanner-acceptedAll &,
    .gdprCookieBanner-acceptedAll & {
      padding-bottom: calc(${cookieBannerHeight}px + 1em);
    }
  }
`;

/*
  These styles overrides are necessary due to the 100vh height 
  of the half sheet in the WPS flow. This helps to place
  close button in the right top corner of the half sheet 
  if withoutCloseButton prop is false.
 */

export const getButtonStylesOverrides = ({
  isFullScreenModal,
  theme,
}: {
  isFullScreenModal: boolean;
  theme: AppTheme.WeasleyTheme;
}): SerializedStyles | boolean => css`
  top: ${isFullScreenModal && '10px'};
  right: ${isFullScreenModal && '10px'};
  width: ${theme.button.closeButton.width};
  height: ${theme.button.closeButton.height};
  padding: ${theme.button.closeButton.padding};
  border-radius: ${theme.button.closeButton.borderRadius};
  color: ${theme.button.closeButton.color};
  cursor: pointer;

  &:hover {
    background-color: ${theme.button.closeButton.hover.background};
    color: ${theme.button.closeButton.hover.color};
  }

  &:focus {
    background-color: ${theme.button.closeButton.focus.background};
    border: ${theme.button.closeButton.focus.border};
    border-radius: ${theme.button.closeButton.focus.borderRadius};
    box-shadow: ${theme.button.closeButton.focus.boxShadow};
  }
`;

export const getOverlayStyleOverrides = (
  mobileQueryWidth: number,
): SerializedStyles => css`
  height: 100%;
  align-items: flex-end;
  display: flex;
  justify-content: center;

  @media (min-width: ${mobileQueryWidth + 1}px) {
    align-items: center;
  }
`;
